import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  computed,
  effect,
  Renderer2,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";
import { A11yModule } from "@angular/cdk/a11y";

import { AnnouncementService } from "../../../../core/services/announcement.service";

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full" | "auto";
export type ModalVariant = "default" | "danger" | "success";

@Component({
  selector: "ui-modal",
  standalone: true,
  // A11yModule provides cdkTrapFocus / cdkTrapFocusAutoCapture used by the
  // dialog container in the template (GAP-PM-031 / GAP-PM-028) so focus is
  // captured into and trapped within the dialog while it is open.
  imports: [A11yModule],
  templateUrl: "./modal.component.html",
  styleUrls: ["./modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalComponent implements OnInit, OnDestroy {
  // Internally-mutated inputs: aliased input + internal writable signal + effect
  readonly isOpenInput = input<boolean>(false, { alias: "isOpen" });
  readonly isOpen = signal(false);

  readonly titleInput = input<string>("", { alias: "title" });
  readonly title = signal("");

  readonly showCloseButtonInput = input<boolean>(true, {
    alias: "showCloseButton",
  });
  readonly showCloseButton = signal(true);

  readonly closeOnBackdropInput = input<boolean>(true, {
    alias: "closeOnBackdrop",
  });
  readonly closeOnBackdrop = signal(true);

  // Pure signal inputs
  readonly subtitle = input<string | undefined>(undefined);
  readonly size = input<ModalSize>("md");
  readonly variant = input<ModalVariant>("default");
  readonly closeOnEscape = input<boolean>(true);
  readonly centered = input<boolean>(true);
  readonly ariaLabelledBy = input<string | undefined>(undefined);
  readonly ariaDescribedBy = input<string | undefined>(undefined);

  // Razor UI dialog compatibility aliases (setter-style inputs)
  // visible aliases isOpen 1:1
  readonly visibleInput = input<boolean | undefined>(undefined, {
    alias: "visible",
  });
  // header aliases title 1:1
  readonly headerInput = input<string | undefined>(undefined, {
    alias: "header",
  });
  // closable aliases showCloseButton 1:1
  readonly closableInput = input<boolean | undefined>(undefined, {
    alias: "closable",
  });
  // modal: inverted alias for closeOnBackdrop (modal=true means no backdrop close)
  readonly modalInput = input<boolean | undefined>(undefined, {
    alias: "modal",
  });
  // dismissableMask aliases closeOnBackdrop 1:1
  readonly dismissableMaskInput = input<boolean | undefined>(undefined, {
    alias: "dismissableMask",
  });

  readonly styleClass = input<string | undefined>(undefined); // For migration compatibility, ignored

  readonly closed = output<void>();
  readonly isOpenChange = output<boolean>();
  readonly visibleChange = output<boolean>(); // Alias for isOpenChange
  readonly onHide = output<void>(); // backward compatibility

  // Backward-compatibility property accessors (used by tests/consumers via `component.visible = true`, etc.)
  get visible(): boolean {
    return this.isOpen();
  }
  set visible(value: boolean) {
    this.isOpen.set(value);
  }

  get header(): string {
    return this.title();
  }
  set header(value: string) {
    this.title.set(value);
  }

  get closable(): boolean {
    return this.showCloseButton();
  }
  set closable(value: boolean) {
    this.showCloseButton.set(value);
  }

  set modal(value: boolean) {
    this.closeOnBackdrop.set(!value);
  }

  set dismissableMask(value: boolean) {
    this.closeOnBackdrop.set(value);
  }

  private announcementService = inject(AnnouncementService);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);

  // GAP-PM-031: tracks whether THIS instance currently holds the body scroll-lock.
  // Guards the add/remove so the lock is idempotent and is only ever removed by the
  // instance that applied it, keeping the lock leak-free across open/close/destroy.
  private scrollLocked = false;

  constructor() {
    // Sync direct inputs into internal writable signals
    effect(() => this.isOpen.set(this.isOpenInput()));
    effect(() => this.title.set(this.titleInput()));
    effect(() => this.showCloseButton.set(this.showCloseButtonInput()));
    effect(() => this.closeOnBackdrop.set(this.closeOnBackdropInput()));

    // Sync alias inputs (only when bound)
    effect(() => {
      const v = this.visibleInput();
      if (v !== undefined) this.isOpen.set(v);
    });
    effect(() => {
      const v = this.headerInput();
      if (v !== undefined) this.title.set(v);
    });
    effect(() => {
      const v = this.closableInput();
      if (v !== undefined) this.showCloseButton.set(v);
    });
    effect(() => {
      const v = this.modalInput();
      if (v !== undefined) this.closeOnBackdrop.set(!v); // inverted
    });
    effect(() => {
      const v = this.dismissableMaskInput();
      if (v !== undefined) this.closeOnBackdrop.set(v);
    });

    // GAP-PM-031: lock body scroll while the modal is open so the page behind the
    // backdrop cannot scroll, then restore it on close. Reactive to isOpen() so it
    // tracks programmatic open/close as well as the input.
    effect(() => {
      if (this.isOpen()) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    });
  }

  readonly backdropClasses = computed<string>(() => {
    return [
      "fixed",
      "inset-0",
      "z-modal-backdrop",
      "bg-black/60",
      "backdrop-blur-sm",
      "animate-fade-in",
    ].join(" ");
  });

  readonly modalContainerClasses = computed<string>(() => {
    const classes = [
      "fixed",
      "inset-0",
      "z-modal",
      "flex",
      "justify-center",
      "pointer-events-none",
    ];

    if (this.centered()) {
      classes.push("items-center");
    } else {
      classes.push("items-start", "pt-[10vh]");
    }

    return classes.join(" ");
  });

  readonly modalClasses = computed<string>(() => {
    const baseClasses = [
      this.size() === "auto" ? "w-auto" : "w-full",
      "rounded-xl",
      "border",
      "border-bb-border",
      "bg-bb-bg-primary",
      "shadow-2xl",
      "shadow-black/30",
      "animate-fade-in-up",
      "pointer-events-auto",
    ];

    return baseClasses.join(" ");
  });

  readonly modalSizeClasses = computed<string>(() => {
    const sizeClasses: Record<ModalSize, string> = {
      sm: "max-w-sm",
      md: "max-w-lg",
      lg: "max-w-2xl",
      xl: "max-w-4xl",
      full: "max-w-[95vw] max-h-[95vh]",
      auto: "max-w-fit",
    };

    return sizeClasses[this.size()];
  });

  readonly headerClasses = computed<string>(() => {
    const classes = [
      "flex",
      "items-start",
      "justify-between",
      "px-6",
      "py-4",
      "border-b",
      "border-bb-border",
    ];

    return classes.join(" ");
  });

  readonly titleClasses = computed<string>(
    () => "text-lg font-semibold text-bb-text-primary",
  );

  readonly subtitleClasses = computed<string>(
    () => "text-sm text-bb-text-secondary mt-0.5",
  );

  readonly closeButtonClasses = computed<string>(() => {
    return [
      "ml-4",
      "flex-shrink-0",
      "p-1.5",
      "rounded-lg",
      "text-bb-text-secondary",
      "hover:text-bb-text-primary",
      "hover:bg-bb-bg-secondary",
      "transition-colors",
      "focus:outline-none",
      "focus-visible:ring-2",
      "focus-visible:ring-bb-accent-blue",
    ].join(" ");
  });

  readonly iconClasses = computed<string>(() => {
    const variantColors: Record<ModalVariant, string> = {
      default: "text-bb-accent-blue bg-bb-accent-blue/10",
      danger: "text-bb-accent-red bg-bb-accent-red/10",
      success: "text-bb-accent-green bg-bb-accent-green/10",
    };

    return `w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mr-4 ${variantColors[this.variant()]}`;
  });

  onBackdropClick(): void {
    if (this.closeOnBackdrop()) {
      this.close();
    }
  }

  ngOnInit(): void {
    if (this.isOpen() && this.title()) {
      this.announcementService.announce(
        `${this.title()} dialog opened`,
        "assertive",
      );
    }
  }

  ngOnDestroy(): void {
    // GAP-PM-031: always release the scroll-lock on teardown so a modal destroyed
    // while still open (e.g. route change) cannot leave the body permanently locked.
    this.unlockBodyScroll();
    if (this.title()) {
      this.announcementService.announce(
        `${this.title()} dialog closed`,
        "polite",
      );
    }
  }

  // GAP-PM-031: body scroll-lock helpers. Add/remove are paired and guarded by
  // `scrollLocked` so the lock is idempotent and only removed by the instance
  // that set it.
  private lockBodyScroll(): void {
    if (this.scrollLocked) return;
    this.renderer.setStyle(this.document.body, "overflow", "hidden");
    this.scrollLocked = true;
  }

  private unlockBodyScroll(): void {
    if (!this.scrollLocked) return;
    this.renderer.removeStyle(this.document.body, "overflow");
    this.scrollLocked = false;
  }

  close(): void {
    this.isOpen.set(false);
    this.isOpenChange.emit(false);
    this.visibleChange.emit(false);
    this.closed.emit();
    this.onHide.emit();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape" && this.closeOnEscape()) {
      this.close();
    }
  }
}

@Component({
  selector: "ui-modal-header",
  standalone: true,
  templateUrl: "./modal-header.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: "flex flex-col space-y-1.5 text-center sm:text-left",
  },
})
export class ModalHeaderComponent {}

@Component({
  selector: "ui-modal-footer",
  standalone: true,
  templateUrl: "./modal-footer.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    // Footer is now a direct flex child of modal, consumers can add justify-* classes
    class:
      "flex flex-col-reverse sm:flex-row gap-3 px-6 py-4 border-t border-bb-border bg-bb-bg-secondary/50 rounded-b-xl flex-shrink-0",
  },
})
export class ModalFooterComponent {}

@Component({
  selector: "ui-modal-content",
  standalone: true,
  templateUrl: "./modal-content.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    // No horizontal padding needed - parent wrapper handles it
    class: "block text-bb-text-primary",
  },
})
export class ModalContentComponent {}
